// ---- Tournament data ----------------------------------------------------
// Pure constants. No DOM, no React — safe to import from node tests.

export const GROUPS = {
  A: ["Mexico", "South Korea", "South Africa", "Czechia"],
  B: ["Canada", "Bosnia and Herzegovina", "Qatar", "Switzerland"],
  C: ["Brazil", "Morocco", "Scotland", "Haiti"],
  D: ["United States", "Australia", "Paraguay", "Turkey"],
  E: ["Germany", "Ecuador", "Ivory Coast", "Curaçao"],
  F: ["Netherlands", "Japan", "Tunisia", "Sweden"],
  G: ["Belgium", "Iran", "Egypt", "New Zealand"],
  H: ["Spain", "Uruguay", "Saudi Arabia", "Cape Verde"],
  I: ["France", "Senegal", "Norway", "Iraq"],
  J: ["Argentina", "Austria", "Algeria", "Jordan"],
  K: ["Portugal", "Colombia", "Uzbekistan", "DR Congo"],
  L: ["England", "Croatia", "Panama", "Ghana"]
};

export const FLAGS = {
  Mexico: "🇲🇽",
  "South Korea": "🇰🇷",
  "South Africa": "🇿🇦",
  Czechia: "🇨🇿",
  Canada: "🇨🇦",
  "Bosnia and Herzegovina": "🇧🇦",
  Qatar: "🇶🇦",
  Switzerland: "🇨🇭",
  Brazil: "🇧🇷",
  Morocco: "🇲🇦",
  Scotland: "🏴󠁧󠁢󠁳󠁣󠁴󠁿",
  Haiti: "🇭🇹",
  "United States": "🇺🇸",
  Australia: "🇦🇺",
  Paraguay: "🇵🇾",
  Turkey: "🇹🇷",
  Germany: "🇩🇪",
  Ecuador: "🇪🇨",
  "Ivory Coast": "🇨🇮",
  "Curaçao": "🇨🇼",
  Netherlands: "🇳🇱",
  Japan: "🇯🇵",
  Tunisia: "🇹🇳",
  Sweden: "🇸🇪",
  Belgium: "🇧🇪",
  Iran: "🇮🇷",
  Egypt: "🇪🇬",
  "New Zealand": "🇳🇿",
  Spain: "🇪🇸",
  Uruguay: "🇺🇾",
  "Saudi Arabia": "🇸🇦",
  "Cape Verde": "🇨🇻",
  France: "🇫🇷",
  Senegal: "🇸🇳",
  Norway: "🇳🇴",
  Iraq: "🇮🇶",
  Argentina: "🇦🇷",
  Austria: "🇦🇹",
  Algeria: "🇩🇿",
  Jordan: "🇯🇴",
  Portugal: "🇵🇹",
  Colombia: "🇨🇴",
  Uzbekistan: "🇺🇿",
  "DR Congo": "🇨🇩",
  England: "🏴󠁧󠁢󠁥󠁮󠁧󠁿",
  Croatia: "🇭🇷",
  Panama: "🇵🇦",
  Ghana: "🇬🇭"
};

export const GROUP_KEYS = Object.keys(GROUPS);

// Canonical flat list of all 48 teams in group order. Index 0..47 is a stable
// id used by the URL hash encoder to compactly reference knockout winners.
export const ALL_TEAMS = GROUP_KEYS.flatMap(g => GROUPS[g]);

// World Football Elo ratings (~June 2026), sourced from eloratings.net /
// footballratings.org. Used to forecast group order and knockout winners.
export const ELO = {
  Spain: 2157,
  Argentina: 2115,
  France: 2063,
  England: 2024,
  Portugal: 1989,
  Colombia: 1982,
  Brazil: 1978,
  Netherlands: 1944,
  Germany: 1939,
  Norway: 1914,
  Croatia: 1912,
  Japan: 1910,
  Switzerland: 1891,
  Uruguay: 1890,
  Senegal: 1869,
  Belgium: 1849,
  Morocco: 1835,
  Canada: 1820,
  "Ivory Coast": 1810,
  Ecuador: 1805,
  Mexico: 1800,
  Turkey: 1800,
  "United States": 1790,
  Sweden: 1788,
  Austria: 1770,
  Iran: 1760,
  Scotland: 1755,
  Australia: 1755,
  Algeria: 1750,
  "South Korea": 1745,
  Paraguay: 1730,
  Panama: 1730,
  Czechia: 1710,
  "Bosnia and Herzegovina": 1700,
  Egypt: 1696,
  Ghana: 1690,
  Uzbekistan: 1670,
  "Saudi Arabia": 1660,
  "DR Congo": 1655,
  Qatar: 1650,
  "South Africa": 1640,
  Tunisia: 1628,
  Iraq: 1620,
  Jordan: 1610,
  Haiti: 1600,
  "Cape Verde": 1578,
  "New Zealand": 1500,
  "Curaçao": 978
};

// ---- Knockout structure -------------------------------------------------
// Round of 32 structure. Each match has two slots.
// A slot is either {t:'winner', g} {t:'runner', g} or {t:'third'}.
// Official FIFA 2026 Round of 32, matches 73–88 (in match-id order).
export const R32_MATCHES = [
  { id: 73, home: { t: "runner", g: "A" }, away: { t: "runner", g: "B" } }, // M73
  { id: 74, home: { t: "winner", g: "E" }, away: { t: "third" } },          // M74
  { id: 75, home: { t: "winner", g: "F" }, away: { t: "runner", g: "C" } }, // M75
  { id: 76, home: { t: "winner", g: "C" }, away: { t: "runner", g: "F" } }, // M76
  { id: 77, home: { t: "winner", g: "I" }, away: { t: "third" } },          // M77
  { id: 78, home: { t: "runner", g: "E" }, away: { t: "runner", g: "I" } }, // M78
  { id: 79, home: { t: "winner", g: "A" }, away: { t: "third" } },          // M79
  { id: 80, home: { t: "winner", g: "L" }, away: { t: "third" } },          // M80
  { id: 81, home: { t: "winner", g: "D" }, away: { t: "third" } },          // M81
  { id: 82, home: { t: "winner", g: "G" }, away: { t: "third" } },          // M82
  { id: 83, home: { t: "runner", g: "K" }, away: { t: "runner", g: "L" } }, // M83
  { id: 84, home: { t: "winner", g: "H" }, away: { t: "runner", g: "J" } }, // M84
  { id: 85, home: { t: "winner", g: "B" }, away: { t: "third" } },          // M85
  { id: 86, home: { t: "winner", g: "J" }, away: { t: "runner", g: "H" } }, // M86
  { id: 87, home: { t: "winner", g: "K" }, away: { t: "third" } },          // M87
  { id: 88, home: { t: "runner", g: "D" }, away: { t: "runner", g: "G" } }  // M88
];

// Round of 16 (M89–M96): which two R32 winners feed each match, by index
// into R32_MATCHES (0=M73 … 15=M88). From the official bracket:
//   M89 = W74 vs W77   M90 = W73 vs W75   M91 = W76 vs W78   M92 = W79 vs W80
//   M93 = W83 vs W84   M94 = W81 vs W82   M95 = W86 vs W88   M96 = W85 vs W87
export const R16_PAIRS = [
  [1, 4],   // M89: M74, M77
  [0, 2],   // M90: M73, M75
  [3, 5],   // M91: M76, M78
  [6, 7],   // M92: M79, M80
  [10, 11], // M93: M83, M84
  [8, 9],   // M94: M81, M82
  [13, 15], // M95: M86, M88
  [12, 14]  // M96: M85, M87
];

// Quarterfinals (M97–M100), indices into R16 (0=M89 … 7=M96):
//   M97 = W89 vs W90   M98 = W93 vs W94   M99 = W91 vs W92   M100 = W95 vs W96
export const QF_PAIRS = [
  [0, 1], // M97:  M89, M90
  [4, 5], // M98:  M93, M94
  [2, 3], // M99:  M91, M92
  [6, 7]  // M100: M95, M96
];

// Semifinals: M101 = W97 vs W98, M102 = W99 vs W100 (indices into QF)
export const SF_PAIRS = [
  [0, 1], // M101: M97, M98
  [2, 3]  // M102: M99, M100
];

export const F_PAIRS = [[0, 1]]; // Final: W101 vs W102

// Base match ids for each built round (R32 is fixed; later rounds derive ids).
export const R16_BASE = 89;
export const QF_BASE = 97;
export const SF_BASE = 101;
export const F_BASE = 103;

// FIFA's third-place assignment: each R32 "third" slot can only be filled by a
// third-placed team from a specific set of groups (and never the slot's own
// group winner's group). Keyed by match id; values are eligible group letters.
export const THIRD_SLOT_ELIGIBILITY = {
  74: ["A", "B", "C", "D", "F"], // M74, host winner 1E
  77: ["C", "D", "F", "G", "H"], // M77, 1I
  79: ["C", "E", "F", "H", "I"], // M79, 1A
  80: ["E", "H", "I", "J", "K"], // M80, 1L
  81: ["B", "E", "F", "I", "J"], // M81, 1D
  82: ["A", "E", "H", "I", "J"], // M82, 1G
  85: ["E", "F", "G", "I", "J"], // M85, 1B
  87: ["D", "E", "I", "J", "L"]  // M87, 1K
};

// The third-slot match ids in bracket order (same order the "third" slots appear
// when scanning R32_MATCHES by id).
export const THIRD_SLOT_IDS = [74, 77, 79, 80, 81, 82, 85, 87];
